/**
 * EmbeddedVideoPlayer - Motor do Player de Vídeos Técnicos de Bancada
 * Honda ADV 150 (K0W / KF43E)
 * Padrão Fábio Akita: Módulos Profundos, Zero Stubs e Ergonomia Dark OLED
 */

import {
  ADV150_TECHNICAL_VIDEOS,
  type TechnicalVideoItem,
  type VideoTimestamp,
  buildEmbedUrl,
  getVideoById,
} from "../data/videoteca.js";

export interface PlayerState {
  activeVideoId: string;
  currentSeconds: number;
  isTheaterMode: boolean;
  isPlaying: boolean;
}

export interface PlayerOptions {
  initialVideoId?: string;
  initialSeconds?: number;
  isTheaterMode?: boolean;
}

export class EmbeddedVideoPlayer {
  private activeVideo: TechnicalVideoItem;
  private currentSeconds: number;
  private isTheaterMode: boolean;
  private isPlaying: boolean;

  constructor(options?: PlayerOptions) {
    const startId = options?.initialVideoId || ADV150_TECHNICAL_VIDEOS[0]?.id || "v_oleo_motor";
    const found = getVideoById(startId) || ADV150_TECHNICAL_VIDEOS[0];
    if (!found) {
      throw new Error("Nenhum vídeo técnico cadastrado na videoteca");
    }
    this.activeVideo = found;
    this.currentSeconds = options?.initialSeconds || 0;
    this.isTheaterMode = options?.isTheaterMode || false;
    this.isPlaying = false;
  }

  public getState(): PlayerState {
    return {
      activeVideoId: this.activeVideo.id,
      currentSeconds: this.currentSeconds,
      isTheaterMode: this.isTheaterMode,
      isPlaying: this.isPlaying,
    };
  }

  public getActiveVideo(): TechnicalVideoItem {
    return this.activeVideo;
  }

  public loadVideo(videoId: string, startSeconds = 0, autoplay = false): void {
    const video = getVideoById(videoId);
    if (!video) {
      throw new Error(`Vídeo não encontrado para o ID: ${videoId}`);
    }
    this.activeVideo = video;
    this.currentSeconds = startSeconds;
    this.isPlaying = autoplay;
  }

  public seekTo(seconds: number): void {
    if (seconds < 0) {
      this.currentSeconds = 0;
    } else {
      this.currentSeconds = seconds;
    }
    this.isPlaying = true;
  }

  public setTheaterMode(enabled: boolean): void {
    this.isTheaterMode = enabled;
  }

  public toggleTheaterMode(): boolean {
    this.isTheaterMode = !this.isTheaterMode;
    return this.isTheaterMode;
  }

  public getEmbedSrc(): string {
    return buildEmbedUrl(this.activeVideo.youtubeVideoId, this.currentSeconds, this.isPlaying);
  }

  /**
   * Renderiza a estrutura HTML do container do player com IFrame e cabeçalho de bancada
   */
  public renderPlayerMarkup(): string {
    const v = this.activeVideo;
    const embedSrc = this.getEmbedSrc();
    const theaterClass = this.isTheaterMode ? "theater-mode" : "";

    return `
      <div class="workshop-player-container ${theaterClass}" id="workshopPlayerContainer" data-video-id="${v.id}">
        <div class="player-header-bar">
          <div class="player-title-info">
            <span class="badge-channel">🎬 ${v.channel}</span>
            <span class="badge-category">${v.subsystemLabel}</span>
            <span class="badge-difficulty diff-${v.difficulty.toLowerCase()}">${v.difficulty}</span>
          </div>
          <div class="player-controls-quick">
            <button class="btn-theater-toggle" id="btnToggleTheater" title="Alternar Modo Teatro de Bancada" aria-label="Modo Teatro">
              ${this.isTheaterMode ? "🗗 Reduzir" : "🗖 Modo Teatro"}
            </button>
            <a href="${v.url}${this.currentSeconds > 0 ? `&t=${this.currentSeconds}s` : ""}" target="_blank" rel="noopener noreferrer" class="btn-external-yt" id="btnYtFallback" title="Abrir vídeo oficial diretamente no YouTube">
              📺 YouTube ↗
            </a>
          </div>
        </div>

        <div class="iframe-aspect-ratio-box">
          <iframe 
            id="workshopIframePlayer"
            src="${embedSrc}" 
            title="${v.title}"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
            allowfullscreen
            loading="lazy">
          </iframe>
        </div>

        <div class="player-meta-summary">
          <h2 class="active-video-h2">${v.title}</h2>
          <p class="active-video-desc">${v.description}</p>
        </div>

        ${this.renderWorkshopBenchPanel()}
      </div>
    `.trim();
  }

  /**
   * Renderiza o painel de bancada com torques, peças OEM e alertas de Gotcha
   */
  public renderWorkshopBenchPanel(): string {
    const v = this.activeVideo;

    // Pílulas de Torques
    const torquesHtml =
      v.torqueSpecs.length > 0
        ? v.torqueSpecs
            .map(
              (t) => `
          <div class="bench-torque-card">
            <div class="torque-val">${t.nm} N.m <span class="kgfm">(${t.kgfm} kgf.m)</span></div>
            <div class="torque-comp">${t.name}</div>
            <div class="torque-tool">Chave: <code>${t.socket}</code></div>
          </div>
        `,
            )
            .join("")
        : '<p class="empty-note">Sem torques críticos declarados para esta etapa.</p>';

    // Pílulas de Peças OEM
    const partsHtml =
      v.relatedPartNumbers.length > 0
        ? v.relatedPartNumbers
            .map(
              (pn) => `
          <a href="#catalogo&part=${pn}" class="bench-part-pill" title="Localizar peça ${pn} no Catálogo EPC">
            <span class="part-icon">🧩</span>
            <span class="part-num">${pn}</span>
            <span class="part-arrow">➔</span>
          </a>
        `,
            )
            .join("")
        : '<p class="empty-note">Procedimento puramente de inspeção ou ajuste.</p>';

    // Ferramentas especiais
    const toolsHtml =
      v.specialTools.length > 0
        ? v.specialTools.map((tool) => `<li class="tool-list-item">🔧 ${tool}</li>`).join("")
        : '<li class="empty-note">Ferramentas manuais padrão de oficina.</li>';

    return `
      <div class="workshop-bench-panel">
        <div class="bench-column">
          <h4 class="bench-col-title">⚡ Torques de Aperto do Procedimento</h4>
          <div class="bench-torques-grid">${torquesHtml}</div>
        </div>

        <div class="bench-column">
          <h4 class="bench-col-title">📦 Peças Genuínas Honda Envolvidas</h4>
          <div class="bench-parts-wrap">${partsHtml}</div>
        </div>

        <div class="bench-column">
          <h4 class="bench-col-title">🛠️ Ferramentas & Instrumentos</h4>
          <ul class="bench-tools-list">${toolsHtml}</ul>
        </div>

        <div class="bench-gotcha-banner">
          <strong>🚨 GOVERNANÇA ANTI-GAMBIARRA (CONCESSIONÁRIA):</strong>
          <p>${v.criticalGotcha}</p>
        </div>
      </div>
    `.trim();
  }

  /**
   * Renderiza a tabela/lista interativa de marcadores temporais
   */
  public renderTimestampsList(): string {
    const v = this.activeVideo;
    const items = v.timestamps
      .map((ts: VideoTimestamp) => {
        const idx = v.timestamps.indexOf(ts);
        const nextTimestamp = v.timestamps[idx + 1];
        const isCurrent =
          this.currentSeconds >= ts.seconds &&
          (nextTimestamp !== undefined ? this.currentSeconds < nextTimestamp.seconds : true);

        return `
        <div class="timestamp-row ${isCurrent ? "current-step" : ""}" data-seconds="${ts.seconds}">
          <button 
            class="timestamp-seek-btn" 
            onclick="seekVideo(${ts.seconds})"
            data-seconds="${ts.seconds}"
            aria-label="Pular para ${ts.time} - ${ts.step}">
            ▶ ${ts.time}
          </button>
          <div class="timestamp-info">
            <div class="timestamp-step-name">${ts.step}</div>
            <div class="timestamp-step-detail">${ts.detail}</div>
          </div>
        </div>
      `;
      })
      .join("");

    return `
      <div class="timestamps-interactive-container" id="timestampsListContainer">
        <div class="timestamps-header">
          <h3 class="timestamps-h3">⏱️ Roteiro Cronológico de Bancada (${v.timestamps.length} Etapas)</h3>
          <span class="timestamps-hint">Toque na minutagem para avançar o vídeo</span>
        </div>
        <div class="timestamps-rows-scroll">
          ${items}
        </div>
      </div>
    `.trim();
  }
}
